import { Instagram, Facebook, Globe } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogoMark } from "@/components/relyn/Nav";
import { scrollToSection } from "@/lib/scrollToSection";

// X (Twitter) icon — inline SVG, since lucide's "Twitter" is the old bird logo.
function XIcon({ className = "w-4 h-4" }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M18.244 2H21l-6.51 7.44L22 22h-6.86l-4.77-6.24L4.8 22H2.04l6.96-7.96L2 2h6.98l4.32 5.71L18.244 2Zm-2.4 18h1.9L8.24 4h-2L15.844 20Z" />
    </svg>
  );
}

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="relative pt-20 pb-12 border-t border-white/[0.06]" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-[1.4fr_1fr_1fr_1fr] gap-10">
          <div>
            <Link to="/" className="inline-flex items-center gap-3.5" data-testid="footer-logo">
              <LogoMark size={56} />
              <span className="text-white text-2xl md:text-3xl tracking-tight" style={{ fontFamily: "Outfit", fontWeight: 600 }}>
                Relyn
              </span>
            </Link>
            <p className="mt-4 text-sm text-[#C0C7D1] leading-relaxed max-w-xs">
              The personal growth app for men. Understand yourself. Strengthen your relationships.
            </p>
            <div className="mt-6 flex items-center gap-2">
              {[
                { icon: Instagram, label: "instagram", ariaLabel: "Instagram — Relyn App", href: "https://instagram.com/relynapp" },
                { icon: XIcon, label: "x", ariaLabel: "X — Relyn App", href: "https://x.com/relynapp" },
                { icon: Facebook, label: "facebook", ariaLabel: "Facebook — Relyn App", href: "https://facebook.com/relynapp" },
                { icon: Globe, label: "website", ariaLabel: "Website — Relyn App", href: "https://www.relynapp.com" },
              ].map(({ icon: I, label, ariaLabel, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={ariaLabel}
                  title="Relyn App"
                  data-testid={`footer-social-${label}`}
                  className="w-9 h-9 rounded-full glass-soft border border-white/10 flex items-center justify-center text-[#C0C7D1] hover:text-white hover:border-[#2D8CFF]/50 transition-colors"
                >
                  <I className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          <FooterCol
            title="Product"
            links={[
              { label: "Features", href: "#features" },
              { label: "How it works", href: "#how-it-works" },
              { label: "App preview", href: "#preview" },
              { label: "FAQ", href: "#faq" },
            ]}
          />
          <FooterCol
            title="Company"
            links={[
              { label: "About", href: "#why-relyn" },
              { label: "Press", href: "#press" },
              { label: "Support", to: "/#contact" },
              { label: "Beta program", href: "#waitlist" },
            ]}
          />
          <FooterCol
            title="Legal"
            links={[
              { label: "Privacy Policy", to: "/privacy" },
              { label: "Terms of Service", to: "/terms" },
              { label: "Cookies", to: "/cookies" },
              { label: "Security", to: "/security" },
            ]}
          />
        </div>

        {/* Disclaimer */}
        <div
          className="mt-12 glass-soft rounded-2xl p-5 md:p-6 max-w-4xl mx-auto text-center"
          data-testid="footer-disclaimer"
        >
          <p className="text-xs md:text-sm text-[#C0C7D1] leading-relaxed">
            Relyn provides tools for self-reflection, personal growth, and relationship
            awareness. Relyn and Leo do not provide medical, mental-health, legal, or
            emergency services.
          </p>
        </div>

        <div className="divider-glow mt-10" />

        <div className="mt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-[#8A99B0]">
          <p data-testid="footer-copyright">© {year} Relyn. All rights reserved.</p>
          <p>
            Crafted with intention by{" "}
            <span className="text-[#C0C7D1]">Jermaine &ldquo;Platinum J&rdquo; Melvin</span>
          </p>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  const navigate = useNavigate();
  const location = useLocation();

  const onSectionClick = (id) => {
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
    } else {
      scrollToSection(id, id === "waitlist" ? { focus: "input" } : undefined);
    }
  };

  return (
    <div>
      <p className="text-xs uppercase tracking-[0.22em] text-[#2D8CFF]">{title}</p>
      <ul className="mt-4 space-y-3">
        {links.map((l) => (
          <li key={l.label}>
            {l.to ? (
              <Link
                to={l.to}
                className="text-sm text-[#C0C7D1] hover:text-white transition-colors tap-target inline-block"
                data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => onSectionClick(l.href.replace(/^#/, ""))}
                className="text-sm text-[#C0C7D1] hover:text-white transition-colors tap-target text-left"
                data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
